import React, { useContext } from 'react';
import './app.scss';
import { ApolloProvider } from '@apollo/client';
import Dashboard from './components/dashboard/dashboard';
import * as firebase from 'firebase/app';
import 'firebase/auth';
import { ConfigProvider } from 'antd';
import esES from 'antd/lib/locale/es_ES';
import { Device, MainContext } from './contexts/mainContext';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import { useState, useEffect } from 'react';
import { useAuth, LoggedInStatus, useSession } from './hooks/firebase';
import { WrappedNormalLoginForm } from './components/login/login';
import { Spin, message } from 'antd';
import { getClient, setupClient } from './apollo';
import { gql } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import {
  ServicesContext,
  useServicesContext,
} from './contexts/servicesContext';
import { PasswordReset } from './components/passwordReset/passwordReset';
import { Link, RouteComponentProps } from 'react-router-dom';
import {
  useGetFrontendVersionQuery,
  // useOnUpdateMeSubscription,
} from './graphql/datamodel.gen';
import { ClientsContext, useClientsContext } from './contexts/clientsContext';
import Rate from './components/rate/rate';

firebase.initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
});

firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);

const fetchAllServices = async (
  departureDateMin: string,
  departureDateMax: string,
): Promise<Array<any>> => {
  // @ts-ignore
  const response = await getClient().query({
    query: gql`
      {
        services(
          departureDateMin: "${departureDateMin}"
          departureDateMax: "${departureDateMax}"
        ) {
            id
        }
      }
    `,
  });

  return (response.data as any).services;
};

const openErrorMessage = (text: string, key: string) => {
  message.error({ content: text, key });
};

const App: React.FC = () => {
  const [state, setState] = useState<{ graphqlClient: any }>({
    graphqlClient: undefined,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // @ts-ignore
  useEffect(() => {
    setupClient().then((client) =>
      setState({
        graphqlClient: client,
      }),
    );
  }, []);

  if (!state.graphqlClient) {
    return <Spin size="large" />;
  }

  return <Application />;
};

const Application: React.FC = () => {
  const {
    loggedInStatus,
    firebaseUser,
    firebaseIdToken,
    isAdmin,
    userId,
    volcanoClientId,
    isClient,
    isDriver,
    isPartOfDrivingCompany,
    clientRole,
    clientId,
    name,
  } = useAuth();
  const servicesContext = useServicesContext();
  const clientsContext = useClientsContext();

  const isPhone = useMediaQuery({ minWidth: 0, maxWidth: '43em' });
  const isTablet = useMediaQuery({ minWidth: '43em', maxWidth: '62em' });
  const isDesktop = useMediaQuery({ minWidth: '62em', maxWidth: '110em' });
  const isLargeDesktop = useMediaQuery({ minWidth: '110em' });

  let device: Device;

  if (isDesktop || isLargeDesktop) {
    device = {
      type: 'desktop',
      bigScreen: isLargeDesktop,
    };
  }

  if (isTablet || isPhone) {
    device = {
      type: 'phone',
      bigScreen: isTablet,
    };
  }

  if (!isPhone && !isTablet && !isLargeDesktop && !isDesktop) {
    device = {
      type: 'desktop',
      bigScreen: false,
    };
  }

  if (loggedInStatus === LoggedInStatus.INITIALIZING) {
    return <Spin size="large" />;
  }

  if (loggedInStatus === LoggedInStatus.UNKNOWN_FAILURE) {
    openErrorMessage('Error desconocido: 040', 'loading');
  }

  return (
    <ConfigProvider locale={esES}>
      <ApolloProvider client={getClient()}>
        <MainContext.Provider
          value={{
            firebaseUser,
            firebaseIdToken,
            isAdmin,
            volcanoClientId,
            isClient,
            clientRole,
            clientId,
            name,
            userId,
            // @ts-ignore
            device,
            isDriver,
            isPartOfDrivingCompany,
          }}
        >
          <ClientsContext.Provider value={{ ...clientsContext }}>
            <ServicesContext.Provider value={{ ...servicesContext }}>
              <Router>
                <Switch>
                  <Route path="/services-list">
                    <ServicesList />
                  </Route>
                  <Route path="/login">
                    <LoginPage />
                  </Route>
                  <Route path="/password-reset" component={PasswordResetPage} />
                  <PrivateRoute path="/profile">
                    <Dashboard preselectedMenu={'profile'} />
                  </PrivateRoute>
                  {isAdmin && (
                    <Switch>
                      <PrivateRoute exact path="/budgets">
                        <Dashboard preselectedMenu={'budgets'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/drivers">
                        <Dashboard preselectedMenu={'drivers'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/clients">
                        <Dashboard preselectedMenu={'clients'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/vehicles">
                        <Dashboard preselectedMenu={'vehicles'} />
                      </PrivateRoute>
                      <PrivateRoute exact path={'/rates'}>
                        <Dashboard preselectedMenu={'rates'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/driving_companies">
                        <Dashboard preselectedMenu={'driving_companies'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/service_offers">
                        <Dashboard preselectedMenu={'service_offers'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/services">
                        <Dashboard preselectedMenu={'services'} />
                      </PrivateRoute>
                      <Redirect
                        to={{
                          pathname: '/services',
                        }}
                      />
                    </Switch>
                  )}
                  {/* {(isClient || isAdmin) && (
                    <Switch>
                      <PrivateRoute exact path="/budgets">
                        <Dashboard preselectedMenu={'budgets'} />
                      </PrivateRoute>
                    </Switch>
                  )} */}
                  {isClient && clientRole === 'EMPLOYEE' && (
                    <Switch>
                      <PrivateRoute exact path="/services">
                        <Dashboard preselectedMenu={'services'} />
                      </PrivateRoute>
                      <Redirect
                        to={{
                          pathname: '/services',
                        }}
                      />
                    </Switch>
                  )}
                  {isClient && clientRole === 'ADMIN' && (
                    <Switch>
                      <PrivateRoute exact path="/budgets">
                        <Dashboard preselectedMenu={'budgets'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/services">
                        <Dashboard preselectedMenu={'services'} />
                      </PrivateRoute>
                      <PrivateRoute exact path="/employees">
                        <Dashboard preselectedMenu={'client_employees'} />
                      </PrivateRoute>
                      <Redirect
                        to={{
                          pathname: '/services',
                        }}
                      />
                    </Switch>
                  )}
                  {isDriver && isPartOfDrivingCompany && (
                    <Switch>
                      <PrivateRoute exact path="/offers">
                        <Dashboard preselectedMenu={'offers'} />
                      </PrivateRoute>
                      <Redirect
                        to={{
                          pathname: '/offers',
                        }}
                      />
                    </Switch>
                  )}
                  <PrivateRoute path="/">
                    <Dashboard />
                  </PrivateRoute>
                </Switch>
              </Router>
            </ServicesContext.Provider>
          </ClientsContext.Provider>
        </MainContext.Provider>
      </ApolloProvider>
    </ConfigProvider>
  );
};

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
const PrivateRoute: React.FC<{
  children: any;
  path: string;
  exact?: boolean;
}> = ({ children, path, exact }) => {
  const session = useSession();

  return (
    <Route
      path={path}
      exact={exact}
      render={({ location }) =>
        !!session ? (
          children
        ) : session ? (
          <Redirect
            to={{
              pathname: '/login',
            }}
          />
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const ServicesList: React.FC = () => {
  const [serviceIds, setServiceIds] = useState([]);

  useEffect(() => {
    if (!serviceIds || serviceIds.length === 0) {
      getServiceIds();
    }
  });

  async function getServiceIds() {
    const serviceIds = (
      await fetchAllServices(
        moment().subtract(2, 'days').format('YYYY-MM-DD'),
        moment().add(2, 'days').format('YYYY-MM-DD'),
      )
    ).map((service: { id: any }) => service.id);
    setServiceIds(serviceIds as never[]);
  }

  return (
    <ul>
      {(() => {
        return serviceIds.map((id) => (
          <li>
            <a href={`mvipsmobile://mvips.net/?serviceId=${id}`}>{id}</a>
          </li>
        ));
      })()}
    </ul>
  );
};

const PasswordResetPage: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const [loading, setLoading] = useState(false);

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  return (
    <div className="login-container">
      <PasswordReset
        {...props}
        resetPassword={resetPassword}
        loading={loading}
      />
    </div>
  );
};

const LoginPage: React.FC = () => {
  let history = useHistory();
  let location = useLocation();
  const session = useSession();

  // @ts-ignore
  let { from } = location.state || { from: { pathname: '/' } };

  if (session) {
    history.replace(from);
  }

  return (
    <div className="login-container">
      <WrappedNormalLoginForm />
    </div>
  );
};

export default App;
